import React from 'react';
import dayjs from 'dayjs';
import type { ComparisonOption, ComparisonWithRecommendations } from '@/services/bidAnalysis.service';
import { projectLabel } from '@/utils/projectDisplay';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';
import type { PrintColumn } from '@/print-engine';

export interface PrintableBidAnalysisProps {
  analysisNumber: string;
  inquiryNumber: string;
  status: string;
  createdAt?: string;
  recommendationNotes?: string;
  approvalNotes?: string;
  comparison: ComparisonWithRecommendations | null;
  selections: Array<{
    supplier_inquiry_line_id: string;
    supplier_quotation_line_id: string;
    justification?: string;
  }>;
  quotations: Array<{ id: string; supplier?: { supplier_name?: string } }>;
  onClose?: () => void;
}

/** Bid analysis print — shared pagination engine with restored supplier bid columns. */
export const PrintableBidAnalysis: React.FC<PrintableBidAnalysisProps> = ({
  analysisNumber,
  inquiryNumber,
  status,
  createdAt,
  recommendationNotes,
  approvalNotes,
  comparison,
  selections,
  quotations,
  onClose,
}) => {
  const inquiryLines = comparison?.inquiryLines ?? [];
  const lineComparisons = comparison?.lineComparisons ?? {};
  const getSelection = (inquiryLineId: string) =>
    selections.find((s) => s.supplier_inquiry_line_id === inquiryLineId);

  const supplierNames = Array.from(
    new Set(quotations.map((q) => (q.supplier as any)?.supplier_name ?? q.id))
  ).slice(0, 4);

  const columns: PrintColumn[] = [
    { key: 'sno', label: 'Line' },
    { key: 'code', label: 'Item' },
    { key: 'description', label: 'Description' },
    { key: 'qty', label: 'Qty', align: 'right' },
    { key: 'uom', label: 'UOM' },
    { key: 'project', label: 'Project' },
  ];

  for (let i = 0; i < supplierNames.length; i += 1) {
    const sKey = `s${i}`;
    columns.push(
      { key: `${sKey}_price`, label: `${supplierNames[i]} Price`, align: 'right' },
      { key: `${sKey}_total`, label: 'Total', align: 'right' },
      { key: `${sKey}_lead`, label: 'Lead' },
      { key: `${sKey}_del`, label: 'Delivery' }
    );
  }
  columns.push(
    { key: 'recommended', label: 'Recommended' },
    { key: 'selected', label: 'Selected' },
    { key: 'justification', label: 'Justification' }
  );

  return (
    <EngineCommercialA4Print
      docType="bid_analysis"
      title="BID ANALYSIS"
      documentNumber={analysisNumber || 'BA'}
      partyName={inquiryNumber || ''}
      onClose={onClose}
      landscape
      metaLines={[
        { label: 'Analysis No', value: analysisNumber || '' },
        { label: 'Inquiry', value: inquiryNumber || '' },
        { label: 'Status', value: status || '' },
        {
          label: 'Date',
          value: createdAt
            ? dayjs(createdAt).format('DD MMM YYYY')
            : dayjs().format('DD MMM YYYY'),
        },
      ].filter((m) => m.value)}
      columns={columns}
      lines={inquiryLines.map((line) => {
        const lc = lineComparisons[line.id];
        const options: ComparisonOption[] = lc?.options ?? [];
        const recommendedId = lc?.recommendedQuotationLineId ?? null;
        const recommendedOpt = options.find((o) => o.quotationLineId === recommendedId);
        const sel = getSelection(line.id);
        const selectedOpt = sel
          ? options.find((o) => o.quotationLineId === sel.supplier_quotation_line_id)
          : null;
        const cells: Record<string, string> = {
          code: line.item_code ?? '—',
          qty: String(Number(line.quantity)),
          uom: line.uom ?? '—',
          project: projectLabel(line),
          recommended: recommendedOpt?.supplierName ?? '—',
          selected: selectedOpt?.supplierName ?? '—',
          justification: sel?.justification ?? '—',
        };
        supplierNames.forEach((suppName, i) => {
          const opt = options.find((o) => o.supplierName === suppName);
          const sKey = `s${i}`;
          if (!opt) {
            cells[`${sKey}_price`] = '—';
            cells[`${sKey}_total`] = '—';
            cells[`${sKey}_lead`] = '—';
            cells[`${sKey}_del`] = '—';
            return;
          }
          cells[`${sKey}_price`] = opt.unit_price.toFixed(2);
          cells[`${sKey}_total`] = opt.line_total.toFixed(2);
          cells[`${sKey}_lead`] =
            opt.lead_time_days != null ? `${opt.lead_time_days}d` : '—';
          cells[`${sKey}_del`] = opt.promised_delivery_date
            ? dayjs(opt.promised_delivery_date).format('DD MMM YY')
            : '—';
        });
        return {
          lineNumber: line.line_number,
          description: line.item_description ?? '—',
          cells,
        };
      })}
      finals={[
        { id: 'notes', heightPx: 72 },
        { id: 'sign', heightPx: 56 },
      ]}
      finalBlocks={{
        notes: (
          <div>
            <p>
              <strong>Recommendation notes:</strong> {recommendationNotes || '—'}
            </p>
            <p>
              <strong>Approval notes:</strong> {approvalNotes || '—'}
            </p>
          </div>
        ),
        sign: (
          <div style={{ display: 'flex', gap: 48 }}>
            <div>
              Prepared by
              <br />
              _______________________
            </div>
            <div>
              Approved by (Management)
              <br />
              _______________________
            </div>
          </div>
        ),
      }}
    />
  );
};
